import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography,
} from "@mui/material";
import TableRestaurantOutlined from "@mui/icons-material/TableRestaurantOutlined";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import useEditorMapa from "../../stores/useEditorMapa";
import ConfigMesaTarifa from "../tarifas/ConfigMesaTarifa";
import { fromTarifa, toPayload } from "../../domain/tarifa/payload";
import { errorMesa } from "../../domain/tarifa/validacion";
import { TIPO, VENTA_MESA } from "../../domain/tarifa/schema";
import { COLOR } from "./estilos";

const MAX_TARIFAS = 500;
const botonSx = { textTransform: "none", borderRadius: "8px" };

// ConfigurarMesa edita sólo cómo se vende la mesa (por silla o entera, con
// paquetes y adicionales). El resto de la tarifa se reenvía como está.
const ConfigurarMesa = ({ eventoId, idtarifa, onCerrar }) => {
  const recargarTarifas = useEditorMapa((s) => s.recargarTarifas);
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vigente = true;
    Promise.all([
      api.tarifas.getAll(eventoId, "", MAX_TARIFAS, 1, TIPO.TICKET),
      api.ubicacionesNew.getAllCompletas(eventoId),
    ])
      .then(([tarifas, ubicaciones]) => {
        if (!vigente) return;
        const tickets = tarifas?.data?.tarifas ?? [];
        const tarifa = tickets.find((t) => t.id === idtarifa);
        if (tarifas.status !== "success" || !tarifa) throw new Error("No se encontró la tarifa.");
        const lista = Array.isArray(ubicaciones?.data) ? ubicaciones.data : ubicaciones?.data?.ubicaciones;
        const ubicacion = (Array.isArray(lista) ? lista : []).find((u) => u.id === tarifa.idubicacion);
        if (!ubicacion?.mesas?.length) throw new Error("La tarifa no está en una ubicación con mesas. Publicá el mapa y probá de nuevo.");
        setDatos({ tarifa, tickets, ubicacion });
        setForm(fromTarifa(tarifa));
      })
      .catch((e) => {
        if (!vigente) return;
        toast.error(e.message || "No se pudo abrir la tarifa.");
        onCerrar();
      });
    return () => {
      vigente = false;
    };
  }, [eventoId, idtarifa, onCerrar]);

  const set = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    setError(null);
  };

  const guardar = async () => {
    const encontrado = form.venta_mesa === VENTA_MESA.MESA ? errorMesa(form) : null;
    if (encontrado) {
      setError(encontrado);
      return;
    }
    setGuardando(true);
    try {
      const respuesta = await api.tarifas.updateTarifa(form.id, toPayload(form, { eventoId }));
      if (respuesta.status !== "success") throw new Error(respuesta.message || "No se pudo guardar.");
      toast.success("Mesa configurada.");
      recargarTarifas();
      onCerrar();
    } catch (e) {
      setError(e.message || "No se pudo conectar con el servidor.");
    } finally {
      setGuardando(false);
    }
  };

  if (!datos || !form) {
    return (
      <Dialog open>
        <DialogContent sx={{ p: 4 }}>
          <CircularProgress size={28} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={(_, motivo) => motivo !== "backdropClick" && !guardando && onCerrar()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 18, fontWeight: 700, color: COLOR.titulo }}>
          Venta de mesas
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 13, color: COLOR.textoSuave }}>
          {datos.tarifa.concepto} · {datos.ubicacion.ubicacion}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <ConfigMesaTarifa
          form={form}
          set={set}
          error={null}
          tarifasTickets={datos.tickets}
          ubicacion={datos.ubicacion}
        />
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCerrar} disabled={guardando} sx={{ ...botonSx, color: "#374151" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={guardar}
          disabled={guardando}
          sx={{ ...botonSx, bgcolor: COLOR.acento }}
        >
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigurarMesa;

// BotonConfigurarMesa: sólo para tarifas en sectores con mesas. La tarifa
// tiene que estar publicada en ese sector (y sus mesas guardadas) para que el
// formulario ofrezca la mesa entera.
export const BotonConfigurarMesa = ({ tarifa, sector }) => {
  const eventoId = useEditorMapa((s) => s.eventoId);
  const [abierto, setAbierto] = useState(false);
  const cerrar = useCallback(() => setAbierto(false), []);
  if (!sector || sector.mesas.length === 0) return null;

  const listo = Boolean(sector.id) && sector.id === tarifa.idubicacion && sector.mesas.every((m) => m.id);
  return (
    <>
      <Tooltip title={listo ? "" : "Publicá el mapa para configurar la mesa de esta tarifa."}>
        <span>
          <Button
            size="small"
            startIcon={<TableRestaurantOutlined sx={{ fontSize: 16 }} />}
            onClick={() => setAbierto(true)}
            disabled={!listo}
            sx={{ textTransform: "none", fontSize: 12, px: 1, minWidth: 0, color: COLOR.acento }}
          >
            {tarifa.venta_mesa === "mesa" ? "Editar mesa" : "Configurar mesa"}
          </Button>
        </span>
      </Tooltip>
      {abierto && <ConfigurarMesa eventoId={eventoId} idtarifa={tarifa.id} onCerrar={cerrar} />}
    </>
  );
};

